import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
// import { TrialCountdown } from "@/components/ui/trial-countdown";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { api } from "@/lib/api";
import {
  Book,
  Users,
  ArrowLeftRight,
  FileText,
  CreditCard,
  AlertTriangle,
  Clock,
  UserPlus,
  BookOpen,
  Settings,
  Building2,
  BarChart3,
  UserCheck,
  DollarSign,
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  Library,
  ArrowUpRight,
  Search,
  CheckCircle2,
  Loader2,
} from "lucide-react";

interface BillingPlan {
  id: string;
  name: string;
  price: number | string;
  currency: string;
  billingCycle: string;
  features?: Record<string, unknown>;
  status: string;
  startDate?: string;
  endDate?: string;
}

interface DashboardStats {
  catalog: {
    totalBiblios: number;
    totalItems: number;
    totalDigitalResources: number;
    totalCatalog: number;
  };
  patrons: {
    active: number;
    pendingRegistrations: number;
  };
  staff: {
    active: number;
  };
  circulation: {
    checkoutsToday: number;
    returnsToday: number;
    overdueItems: number;
    activeCheckouts: number;
    pendingReservations: number;
  };
  fines: {
    pending: number;
    totalOutstanding: number;
  };
  branches: number;
  recentCheckouts: Array<{
    id: string;
    title: string;
    barcode: string | null;
    patronName: string;
    status: string;
    checkoutDate: string;
    dueDate: string;
    returnDate: string | null;
  }>;
  recentRegistrations: Array<{
    id: string;
    name: string;
    email: string;
    verified: boolean;
    joinedAt: string;
  }>;
}

export function TenantAdminDashboard() {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = useQuery(
    ["dashboard", "stats", tenant?.id],
    ({ signal }) => api.get<DashboardStats>("/dashboard/stats", { signal }).then((r) => r.data),
    {
      enabled: Boolean(tenant?.id || user),
      retry: 1,
      retryDelay: 400,
    }
  );

  const [assignedPlan, setAssignedPlan] = useState<BillingPlan | null>(null);
  const [availablePlans, setAvailablePlans] = useState<BillingPlan[]>([]);
  const [billingLoading, setBillingLoading] = useState(true);
  const [paying, setPaying] = useState<{ id: string; source: "banner" | "card" } | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadBillingInfo = async () => {
      const tenantId = tenant?.id || user?.memberships?.[0]?.tenantId;
      if (!tenantId) {
        setBillingLoading(false);
        return;
      }
      try {
        const plansResponse = await api.get(`/billing/tenants/${tenantId}/plans`, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const activePlan = plansResponse.data.find((p: BillingPlan) => p.status === "active");
        setAssignedPlan(activePlan || null);
        try {
          const availableResponse = await api.get("/billing/plans/available", {
            signal: controller.signal,
          });
          if (!controller.signal.aborted) setAvailablePlans(availableResponse.data || []);
        } catch {
          if (!controller.signal.aborted) setAvailablePlans([]);
        }
      } catch {
        // billing info optional
      } finally {
        if (!controller.signal.aborted) setBillingLoading(false);
      }
    };

    loadBillingInfo();
    return () => controller.abort();
  }, [tenant, user]);

  const reloadBilling = async () => {
    const tenantId = tenant?.id || user?.memberships?.[0]?.tenantId;
    if (!tenantId) return;
    try {
      const res = await api.get(`/billing/tenants/${tenantId}/plans`);
      const active = res.data.find((p: BillingPlan) => p.status === "active");
      setAssignedPlan(active || null);
    } catch {
      /* ignore */
    }
  };

  const handlePayPlan = async (planId: string, source: "banner" | "card") => {
    setPaying({ id: planId, source });
    try {
      await api.post(`/billing/plans/${planId}/pay`);
      toast.success("Plan activated successfully!");
      await reloadBilling();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to activate plan");
    } finally {
      setPaying(null);
    }
  };

  const isOnTrial = tenant?.status === "trial";
  const trialEndDate = tenant?.trialEndDate ? new Date(tenant.trialEndDate) : null;
  const trialEnded = trialEndDate ? new Date() > trialEndDate : false;
  const isTrialActive = isOnTrial && !trialEnded;

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const fmt = (n: number) => n.toLocaleString();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {greeting()}
            {user?.firstName ? `, ${user.firstName}` : ""}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {tenant?.name || "Library"} &mdash; Overview
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/setup")}>
            <Settings className="w-4 h-4 mr-1.5" />
            Settings
          </Button>
          <Button size="sm" onClick={() => navigate("/catalog")}>
            <Book className="w-4 h-4 mr-1.5" />
            Catalog
          </Button>
        </div>
      </div>

      {/* Trial Countdown */}
      {/* {isTrialActive && trialEndDate && (
        <TrialCountdown trialEndDate={trialEndDate} trialStartDate={tenant?.trialStartDate} />
      )} */}

      {/* Billing Plan Card */}
      {!billingLoading && assignedPlan && (
        <Card className="bg-emerald-50/50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-900 text-sm">
                    Active: {assignedPlan.name}
                  </p>
                  <p className="text-xs text-emerald-700">
                    {assignedPlan.currency}{" "}
                    {typeof assignedPlan.price === "number"
                      ? assignedPlan.price.toLocaleString()
                      : assignedPlan.price}
                    {" / "}
                    {assignedPlan.billingCycle}
                    {assignedPlan.endDate &&
                      ` · Renews ${new Date(assignedPlan.endDate).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePayPlan(assignedPlan.id, "banner")}
                disabled={paying !== null}
                className="gap-2 bg-white"
              >
                {paying?.id === assignedPlan.id && paying.source === "banner" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Pay Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      {!billingLoading && (!assignedPlan || isOnTrial) && availablePlans.length > 0 && (
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold text-slate-900">
              {assignedPlan ? "Upgrade Plan" : "Select a Billing Plan"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {availablePlans.map((plan) => (
                <Card
                  key={plan.id}
                  className="p-4 border border-slate-200 hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-heading font-bold text-sm text-slate-900">{plan.name}</h3>
                    <CreditCard className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-xl font-bold text-slate-900">
                    {plan.currency}{" "}
                    {typeof plan.price === "number" ? plan.price.toLocaleString() : plan.price}
                  </p>
                  <p className="text-xs text-slate-500 mb-3">per {plan.billingCycle}</p>
                  <Button
                    onClick={() => handlePayPlan(plan.id, "card")}
                    disabled={paying !== null}
                    className="w-full gap-2"
                    size="sm"
                    variant={assignedPlan?.id === plan.id ? "outline" : "default"}
                  >
                    {paying?.id === plan.id && paying.source === "card" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    {assignedPlan?.id === plan.id ? "Current" : "Select"}
                  </Button>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Skeleton loading state */}
      {statsLoading && <DashboardSkeleton />}

      {/* Error state */}
      {statsError && !statsLoading && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-red-800 mb-2">
              Failed to load dashboard statistics
            </p>
            <Button variant="outline" size="sm" onClick={() => void refetchStats()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ─── Dashboard content ─── */}
      {stats && (
        <>
          {/* Stat Strip — single unified container instead of separate cards */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
              <StripMetric
                label="Catalog Items"
                value={fmt(stats.catalog.totalCatalog)}
                icon={<Library className="w-4 h-4 text-primary" />}
                onClick={() => navigate("/catalog")}
              />
              <StripMetric
                label="Physical Items"
                value={fmt(stats.catalog.totalItems)}
                icon={<Book className="w-4 h-4 text-primary" />}
                onClick={() => navigate("/catalog")}
              />
              <StripMetric
                label="Active Patrons"
                value={fmt(stats.patrons.active)}
                icon={<Users className="w-4 h-4 text-primary" />}
                onClick={() => navigate("/patrons")}
              />
              <StripMetric
                label="Active Staff"
                value={fmt(stats.staff.active)}
                icon={<ShieldCheck className="w-4 h-4 text-primary" />}
                onClick={() => navigate("/setup/staff")}
              />
              <StripMetric
                label="Active Checkouts"
                value={fmt(stats.circulation.activeCheckouts)}
                icon={<BookOpen className="w-4 h-4 text-primary" />}
              />
              <StripMetric
                label="Branches"
                value={fmt(stats.branches)}
                icon={<Building2 className="w-4 h-4 text-primary" />}
                onClick={() => navigate("/setup/branches")}
              />
            </div>
          </div>

          {/* Main grid — asymmetric 3/5 + 2/5 layout */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left column — wider, primary content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Today's Circulation */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-slate-400" />
                    Today's Circulation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowLeftRight className="w-4 h-4 text-primary" />
                        <span className="text-xs font-medium text-primary">Checkouts</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {stats.circulation.checkoutsToday}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowLeftRight className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-medium text-emerald-700">Returns</span>
                      </div>
                      <p className="text-2xl font-bold text-emerald-900">
                        {stats.circulation.returnsToday}
                      </p>
                    </div>
                  </div>
                  {/* Inline circulation pulse */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 pt-3 border-t border-slate-100">
                    <CirculationMetric
                      label="Active"
                      value={stats.circulation.activeCheckouts}
                      color="text-slate-900"
                    />
                    <CirculationMetric
                      label="Overdue"
                      value={stats.circulation.overdueItems}
                      color={stats.circulation.overdueItems > 0 ? "text-red-600" : "text-slate-900"}
                    />
                    <CirculationMetric
                      label="Reservations"
                      value={stats.circulation.pendingReservations}
                      color="text-slate-900"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Collection Overview — stacked bar breakdown */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-slate-400" />
                      Collection Overview
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/catalog")}
                      className="text-xs"
                    >
                      View Catalog
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <CollectionBreakdown catalog={stats.catalog} />
                </CardContent>
              </Card>

              {/* Financial Overview */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-400" />
                    Financial Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-0">
                    <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Outstanding Fines</span>
                      <span
                        className={`text-sm font-semibold ${stats.fines.totalOutstanding > 0 ? "text-amber-600" : "text-slate-900"}`}
                      >
                        ₦{stats.fines.totalOutstanding.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Pending Fine Notices</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {stats.fines.pending}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Digital Resources</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {fmt(stats.catalog.totalDigitalResources)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2.5">
                      <span className="text-sm text-slate-600">Bibliographic Records</span>
                      <span className="text-sm font-semibold text-slate-900">
                        {fmt(stats.catalog.totalBiblios)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right column — narrower, secondary content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Needs Attention */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Needs Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {stats.circulation.overdueItems > 0 && (
                      <AttentionItem
                        icon={<Clock className="w-4 h-4 text-red-500" />}
                        label="Overdue items"
                        value={stats.circulation.overdueItems}
                        variant="danger"
                      />
                    )}
                    {stats.patrons.pendingRegistrations > 0 && (
                      <AttentionItem
                        icon={<UserCheck className="w-4 h-4 text-amber-500" />}
                        label="Pending registrations"
                        value={stats.patrons.pendingRegistrations}
                        variant="warning"
                        onClick={() => navigate("/admin/registrations")}
                      />
                    )}
                    {stats.circulation.pendingReservations > 0 && (
                      <AttentionItem
                        icon={<Clock className="w-4 h-4 text-primary" />}
                        label="Pending reservations"
                        value={stats.circulation.pendingReservations}
                        variant="info"
                      />
                    )}
                    {stats.fines.pending > 0 && (
                      <AttentionItem
                        icon={<DollarSign className="w-4 h-4 text-amber-500" />}
                        label={`Outstanding fines (₦${stats.fines.totalOutstanding.toFixed(2)})`}
                        value={stats.fines.pending}
                        variant="warning"
                      />
                    )}
                    {stats.circulation.overdueItems === 0 &&
                      stats.patrons.pendingRegistrations === 0 &&
                      stats.circulation.pendingReservations === 0 &&
                      stats.fines.pending === 0 && (
                        <div className="text-center py-4">
                          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          </div>
                          <p className="text-sm font-medium text-emerald-700">
                            All clear &mdash; nothing needs attention.
                          </p>
                        </div>
                      )}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base font-semibold text-slate-900">
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <QuickAction
                      icon={<Book className="w-4 h-4" />}
                      label="Add to Catalog"
                      description="ISBN lookup or manual entry"
                      onClick={() => navigate("/catalog/isbn")}
                      highlighted
                    />
                    <QuickAction
                      icon={<Search className="w-4 h-4" />}
                      label="Search Catalog"
                      description="Find items in the library"
                      onClick={() => navigate("/catalog")}
                    />
                    <QuickAction
                      icon={<UserPlus className="w-4 h-4" />}
                      label="Manage Patrons"
                      description="View and manage library members"
                      onClick={() => navigate("/patrons")}
                    />
                    <QuickAction
                      icon={<FileText className="w-4 h-4" />}
                      label="Generate Report"
                      description="Cataloging productivity reports"
                      onClick={() => navigate("/admin/reports/cataloging")}
                    />
                    <QuickAction
                      icon={<Users className="w-4 h-4" />}
                      label="Manage Staff"
                      description="Staff members & roles"
                      onClick={() => navigate("/setup/staff")}
                    />
                    <QuickAction
                      icon={<Settings className="w-4 h-4" />}
                      label="Library Settings"
                      description="Configure your library"
                      onClick={() => navigate("/setup")}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Recent Circulation — full-width table */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4 text-slate-400" />
                  Recent Circulation
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/circulation")}
                  className="text-xs"
                >
                  View All
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {stats.recentCheckouts.length === 0 ? (
                <div className="text-center py-8">
                  <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-medium text-slate-900">No activity yet</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Circulation transactions will appear here
                  </p>
                </div>
              ) : (
                <div>
                  {/* Table header */}
                  <div className="hidden sm:grid grid-cols-12 gap-3 px-3 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider border-b border-slate-100">
                    <div className="col-span-5">Title</div>
                    <div className="col-span-3">Patron</div>
                    <div className="col-span-2 text-center">Status</div>
                    <div className="col-span-2 text-right">Date</div>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {stats.recentCheckouts.slice(0, 7).map((c) => (
                      <div
                        key={c.id}
                        className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-3 items-center px-3 py-2.5 hover:bg-slate-50 transition-colors rounded-lg"
                      >
                        <div className="sm:col-span-5 flex items-center gap-2.5 min-w-0">
                          <div
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              c.status === "ACTIVE"
                                ? "bg-primary"
                                : c.status === "RETURNED"
                                  ? "bg-emerald-500"
                                  : c.status === "OVERDUE"
                                    ? "bg-red-500"
                                    : "bg-slate-400"
                            }`}
                          />
                          <p className="text-sm font-medium text-slate-900 truncate">{c.title}</p>
                        </div>
                        <div className="sm:col-span-3 min-w-0 pl-4.5 sm:pl-0">
                          <p className="text-sm text-slate-500 truncate">{c.patronName}</p>
                        </div>
                        <div className="sm:col-span-2 text-left sm:text-center pl-4.5 sm:pl-0">
                          <StatusBadge status={c.status} />
                        </div>
                        <div className="sm:col-span-2 text-left sm:text-right pl-4.5 sm:pl-0">
                          <p className="text-xs text-slate-400">
                            {new Date(c.checkoutDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Registrations */}
          {stats.recentRegistrations.length > 0 && (
            <Card className="border border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-slate-400" />
                    Recent Registrations
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    {stats.patrons.pendingRegistrations > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/admin/registrations")}
                        className="text-xs"
                      >
                        Review Pending
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/patrons")}
                      className="text-xs"
                    >
                      View All
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {stats.recentRegistrations.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(r.name || r.email)[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {r.name || r.email}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(r.joinedAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                          {!r.verified && (
                            <span className="ml-1.5 text-amber-600">&middot; Unverified</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Empty state */}
      {stats && stats.catalog.totalCatalog === 0 && stats.patrons.active === 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Library className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
              Let's set up your library!
            </h3>
            <p className="text-sm text-slate-600 mb-6 max-w-md mx-auto">
              Start by adding books to your catalog, configuring your branches, and inviting staff
              members.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate("/catalog/isbn")}>
                <Book className="w-4 h-4 mr-1.5" />
                Add First Book
              </Button>
              <Button variant="outline" onClick={() => navigate("/setup")}>
                <Settings className="w-4 h-4 mr-1.5" />
                Configure Library
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading dashboard">
      {/* Stat strip skeleton */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className="w-9 h-9 rounded-lg bg-primary/5 animate-pulse flex-shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="h-5 w-10 bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-16 bg-slate-50 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main grid skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="h-4 w-36 bg-slate-100 rounded animate-pulse mb-4" />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="h-20 bg-primary/5 rounded-lg animate-pulse" />
              <div className="h-20 bg-emerald-50/40 rounded-lg animate-pulse" />
            </div>
            <div className="pt-3 border-t border-slate-100 flex gap-5">
              <div className="h-3 w-20 bg-slate-50 rounded animate-pulse" />
              <div className="h-3 w-20 bg-slate-50 rounded animate-pulse" />
              <div className="h-3 w-24 bg-slate-50 rounded animate-pulse" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="h-4 w-40 bg-slate-100 rounded animate-pulse mb-4" />
            <div className="h-3 bg-slate-100 rounded-full animate-pulse mb-4" />
            <div className="flex gap-6">
              <div className="h-3 w-28 bg-slate-50 rounded animate-pulse" />
              <div className="h-3 w-24 bg-slate-50 rounded animate-pulse" />
              <div className="h-3 w-28 bg-slate-50 rounded animate-pulse" />
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="h-4 w-32 bg-slate-100 rounded animate-pulse mb-4" />
            <div className="space-y-2">
              <div className="h-12 bg-amber-50/50 rounded-lg animate-pulse" />
              <div className="h-12 bg-slate-50 rounded-lg animate-pulse" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="h-4 w-28 bg-slate-100 rounded animate-pulse mb-3" />
            <div className="space-y-1.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-2">
                  <div className="w-9 h-9 rounded-lg bg-slate-50 animate-pulse flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-3.5 w-24 bg-slate-50 rounded animate-pulse" />
                    <div className="h-2.5 w-32 bg-slate-50/80 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Table skeleton */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="h-4 w-36 bg-slate-100 rounded animate-pulse mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 py-1">
              <div className="w-2 h-2 rounded-full bg-slate-100 animate-pulse flex-shrink-0" />
              <div className="flex-1 h-3.5 bg-slate-50 rounded animate-pulse" />
              <div className="w-16 h-3.5 bg-slate-50 rounded animate-pulse" />
              <div className="w-14 h-5 bg-slate-50 rounded animate-pulse" />
              <div className="w-12 h-3 bg-slate-50 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>

      <span className="sr-only">Loading dashboard statistics...</span>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function StripMetric({
  label,
  value,
  icon,
  onClick,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  const content = (
    <>
      <div className="w-9 h-9 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-lg font-bold text-slate-900 leading-tight">{value}</p>
        <p className="text-xs text-slate-500 truncate">{label}</p>
      </div>
    </>
  );

  const baseClass =
    "flex items-center gap-3 p-4 text-left transition-colors border-b border-slate-100 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0";

  if (onClick) {
    return (
      <button onClick={onClick} className={`${baseClass} hover:bg-slate-50 cursor-pointer`}>
        {content}
      </button>
    );
  }

  return <div className={baseClass}>{content}</div>;
}

function CirculationMetric({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm font-semibold ${color}`}>{value.toLocaleString()}</span>
    </div>
  );
}

function CollectionBreakdown({ catalog }: { catalog: DashboardStats["catalog"] }) {
  const total = catalog.totalCatalog || 1;
  const segments = [
    {
      label: "Bibliographic Records",
      value: catalog.totalBiblios,
      barColor: "bg-primary",
      dotColor: "bg-primary",
    },
    {
      label: "Physical Items",
      value: catalog.totalItems,
      barColor: "bg-primary/50",
      dotColor: "bg-primary/50",
    },
    {
      label: "Digital Resources",
      value: catalog.totalDigitalResources,
      barColor: "bg-primary/20",
      dotColor: "bg-primary/30",
    },
  ];
  const nonZero = segments.filter((s) => s.value > 0);

  if (total <= 1 && catalog.totalCatalog === 0) {
    return (
      <div className="text-center py-6">
        <Library className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        <p className="text-sm font-medium text-slate-900">No items in catalog</p>
        <p className="text-xs text-slate-500 mt-1">
          Add your first book to see the collection breakdown
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Stacked bar */}
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 mb-4">
        {nonZero.map((s, i) => (
          <div
            key={i}
            className={`${s.barColor} transition-all duration-500`}
            style={{ width: `${(s.value / total) * 100}%` }}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {segments.map((s, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <span className={`w-2.5 h-2.5 rounded-full ${s.dotColor} flex-shrink-0`} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{s.value.toLocaleString()}</p>
              <p className="text-xs text-slate-500 truncate">{s.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AttentionItem({
  icon,
  label,
  value,
  variant,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  variant: "danger" | "warning" | "info";
  onClick?: () => void;
}) {
  const colors = {
    danger: "bg-red-50 border-red-100 text-red-700",
    warning: "bg-amber-50 border-amber-200 text-slate-900",
    info: "bg-primary/5 border-primary/10 text-primary",
  };
  const badgeColors = {
    danger: "bg-red-100 text-red-800",
    warning: "border border-amber-200 bg-amber-100 text-slate-900",
    info: "bg-primary/10 text-primary",
  };

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border ${colors[variant]} ${onClick ? "cursor-pointer hover:brightness-95 transition" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-2.5">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badgeColors[variant]}`}>
        {value}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ACTIVE: "bg-primary/10 text-primary",
    RETURNED: "bg-emerald-100 text-emerald-700",
    OVERDUE: "bg-red-100 text-red-700",
    LOST: "bg-slate-100 text-slate-700",
  };
  return (
    <span
      className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${styles[status] || "bg-slate-100 text-slate-600"}`}
    >
      {status}
    </span>
  );
}

function QuickAction({
  icon,
  label,
  description,
  onClick,
  highlighted,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left cursor-pointer group ${
        highlighted ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-slate-50"
      }`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          highlighted
            ? "bg-primary/10 text-primary"
            : "bg-slate-100 text-slate-600 group-hover:bg-primary/5 group-hover:text-primary"
        }`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-medium ${highlighted ? "text-primary" : "text-slate-900"}`}>
          {label}
        </p>
        <p className={`text-xs ${highlighted ? "text-primary/70" : "text-slate-500"}`}>
          {description}
        </p>
      </div>
    </button>
  );
}
